import { Send, Upload } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";

type MBCChatInputProps = {
  isDisabled: boolean;
  onAttach?: () => void;
  onSend: (text: string) => void;
};

export function MBCChatInput({
  isDisabled,
  onAttach,
  onSend,
}: MBCChatInputProps) {
  const [input, setInput] = useState("");

  const canSend = !isDisabled && input.trim().length > 0;

  function handleSubmit() {
    const trimmed = input.trim();

    if (!trimmed || isDisabled) return;
    onSend(trimmed);
    setInput("");
  }

  return (
    <div className="border-t border-border-secondary bg-bg-primary px-3xl py-lg">
      <div className="flex items-end gap-md">
        <div className="flex flex-1 items-center gap-sm rounded-xl border border-border-primary bg-bg-primary px-lg py-sm">
          <textarea
            className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-sm text-md text-text-primary outline-none placeholder:text-text-placeholder"
            disabled={isDisabled}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Type your answer..."
            rows={1}
            value={input}
          />

          <Button
            aria-label="Upload assets"
            className="text-text-secondary hover:text-text-primary"
            isDisabled={isDisabled || !onAttach}
            isIconOnly
            onPress={onAttach}
            size="sm"
            variant="unstyled"
          >
            <Upload className="size-5" />
          </Button>
        </div>

        <Button
          aria-label="Send message"
          className={twMerge(
            "flex size-12 shrink-0 items-center justify-center rounded-lg bg-bg-secondary text-fg-quaternary transition-colors",
            canSend && "text-fg-brand-primary hover:bg-bg-primary-hover",
          )}
          isDisabled={!canSend}
          isIconOnly
          onPress={handleSubmit}
          variant="unstyled"
        >
          <Send className="size-5" />
        </Button>
      </div>
    </div>
  );
}
